import { useEffect, useState, useContext } from "react";
import { Card, Modal, Form, Button, Badge, Alert, Tabs, Tab } from 'react-bootstrap';
import { BsFillPlusSquareFill, BsFillPencilFill, BsTrashFill } from "react-icons/bs";
import Spinner from 'react-bootstrap/Spinner';
import $ from "jquery";
import { useNavigate } from "react-router-dom";
import UserContext from "../../contexts/UserContext";

const MenuCalendar = () => {
    const navigate = useNavigate();
    const { user, setUser } = useContext(UserContext);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [menus, setMenus] = useState({});
    const [show, setShow] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('lunch');
    
    // Form states
    const [lunchItems, setLunchItems] = useState(['']);
    const [dinnerItems, setDinnerItems] = useState(['']);
    const [noLunchThaali, setNoLunchThaali] = useState(false);
    const [noDinnerThaali, setNoDinnerThaali] = useState(false);
    const [lunchComment, setLunchComment] = useState('');
    const [dinnerComment, setDinnerComment] = useState('');
    
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const handleClose = () => {
        setShow(false);
        resetForm();
    };

    const resetForm = (fromNewMenu=false) => {
        setLunchItems(['']);
        setDinnerItems(['']);
        setNoLunchThaali(false);
        setNoDinnerThaali(false);
        setLunchComment('');
        setDinnerComment('');
        !fromNewMenu && setSelectedDate(null);
    };

    const getMenusForMonth = async (year, month) => {
        setIsLoading(true);
        try {
            const response = await $.ajax({
                type: "GET",
                url: `${process.env.REACT_APP_API_URL}/menus/month/${year}/${month}`,
                xhrFields: { withCredentials: true },
                crossDomain: true
            });
            
            if (response.menus) {
                const menusByDate = {};
                response.menus.forEach(menu => {
                    const dateKey = new Date(menu.date).toDateString();
                    menusByDate[dateKey] = menu;
                });
                setMenus(menusByDate);
            }
        } catch (error) {
            console.log("Error fetching menus:", error);
            if (error.status === 401) {
                navigate("/login");
            }
        }
        setIsLoading(false);
    };

    const handleDateClick = (date) => {
        const dateKey = date.toDateString();
        const existingMenu = menus[dateKey];
        
        setSelectedDate(date);
        
        if (existingMenu) {
            // Load existing menu data
            if (existingMenu.lunch) {
                setLunchItems(existingMenu.lunch.items.length > 0 ? existingMenu.lunch.items : ['']);
                setNoLunchThaali(!existingMenu.lunch.isActive);
                setLunchComment(existingMenu.lunch.comment || '');
            }
            
            if (existingMenu.dinner) {
                setDinnerItems(existingMenu.dinner.items.length > 0 ? existingMenu.dinner.items : ['']);
                setNoDinnerThaali(!existingMenu.dinner.isActive);
                setDinnerComment(existingMenu.dinner.comment || '');
            }
        } else {
            // New menu
            resetForm(true);
        }
        
        setShow(true);
    };

    const addLunchItem = () => {
        setLunchItems([...lunchItems, '']);
        setNoLunchThaali(false);
    };

    const addDinnerItem = () => {
        setDinnerItems([...dinnerItems, '']);
        setNoDinnerThaali(false);
    };

    const removeLunchItem = (index) => {
        const newItems = lunchItems.filter((_, i) => i !== index);
        setLunchItems(newItems.length > 0 ? newItems : ['']);
    };

    const removeDinnerItem = (index) => {
        const newItems = dinnerItems.filter((_, i) => i !== index);
        setDinnerItems(newItems.length > 0 ? newItems : ['']);
    };

    const updateLunchItem = (index, value) => {
        const newItems = [...lunchItems];
        newItems[index] = value;
        setLunchItems(newItems);
    };

    const updateDinnerItem = (index, value) => {
        const newItems = [...dinnerItems];
        newItems[index] = value;
        setDinnerItems(newItems);
    };

    const saveMenu = async () => {
        setSaving(true);
        
        // Format date properly to avoid timezone issues
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        const menuData = {
            date: dateString,
            lunch: {
                items: noLunchThaali ? [] : lunchItems.filter(item => item.trim() !== ''),
                isActive: !noLunchThaali,
                comment: noLunchThaali ? lunchComment : ''
            },
            dinner: {
                items: noDinnerThaali ? [] : dinnerItems.filter(item => item.trim() !== ''),
                isActive: !noDinnerThaali,
                comment: noDinnerThaali ? dinnerComment : ''
            }
        };

        try {
            const response = await $.ajax({
                type: "POST",
                url: `${process.env.REACT_APP_API_URL}/menus/save`,
                data: JSON.stringify(menuData),
                contentType: "application/json",
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response.success) {
                // Update local state
                const dateKey = selectedDate.toDateString();
                setMenus(prev => ({
                    ...prev,
                    [dateKey]: response.menu
                }));
                handleClose();
            }
        } catch (error) {
            console.log("Error saving menu:", error);
            alert("Error saving menu. Please try again.");
        }
        
        setSaving(false);
    };

    const deleteMenu = async () => {
        if (!window.confirm("Are you sure you want to delete this menu? All RSVPs for this date will also be deleted.")) {
            return;
        }

        setSaving(true);
        
        // Format date properly to avoid timezone issues
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        try {
            const response = await $.ajax({
                type: "DELETE",
                url: `${process.env.REACT_APP_API_URL}/menus/delete`,
                data: JSON.stringify({ date: dateString }),
                contentType: "application/json",
                xhrFields: { withCredentials: true },
                crossDomain: true
            });

            if (response.success) {
                // Remove from local state
                const dateKey = selectedDate.toDateString();
                setMenus(prev => {
                    const newMenus = { ...prev };
                    delete newMenus[dateKey];
                    return newMenus;
                });
                handleClose();
            }
        } catch (error) {
            console.log("Error deleting menu:", error);
            alert("Error deleting menu. Please try again.");
        }
        
        setSaving(false);
    };

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        
        const days = [];
        
        // Empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }
        
        // Days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateKey = date.toDateString();
            const hasMenu = menus[dateKey];
            const isToday = date.toDateString() === new Date().toDateString();
            
            days.push(
                <div 
                    key={day} 
                    className={`calendar-day ${isToday ? 'today' : ''} ${hasMenu ? 'has-menu' : ''}`}
                    onClick={() => handleDateClick(date)}
                >
                    <div className="day-number">{day}</div>
                    {hasMenu && (
                        <div className="menu-indicators">
                            {hasMenu.lunch?.isActive && hasMenu.lunch.items.length > 0 && (
                                <Badge bg="warning" size="sm">L</Badge>
                            )}
                            {hasMenu.dinner?.isActive && hasMenu.dinner.items.length > 0 && (
                                <Badge bg="info" size="sm">D</Badge>
                            )}
                            {!hasMenu.lunch?.isActive && !hasMenu.dinner?.isActive && (
                                <Badge bg="secondary" size="sm">X</Badge>
                            )}
                        </div>
                    )}
                </div>
            );
        }
        
        return days;
    };

    const navigateMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    useEffect(() => {
        if (!user) {
            const userSession = localStorage.getItem('userSession');
            if (userSession) {
                // Load user data
            } else {
                navigate("/login");
                return;
            }
        }

        if (user && user.role !== 'admin') {
            navigate("/menu"); // Redirect non-admins to regular menu view
            return;
        }

        getMenusForMonth(currentDate.getFullYear(), currentDate.getMonth());
    }, [currentDate, user]);

    if (isLoading) {
        return (
            <div className="mx-auto mt-5 text-center" style={{width: "100px"}}>
                <Spinner style={{color:'brown'}} animation="border" role="status" />
                <br />
                <span>Loading Calendar...</span>
            </div>
        );
    }

    return (
        <>
            <style jsx>{`
                .calendar-container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                }
                .calendar-header {
                    display: flex;
                    justify-content: between;
                    align-items: center;
                    margin-bottom: 20px;
                    background: #f8f9fa;
                    padding: 15px;
                    border-radius: 8px;
                }
                .calendar-grid {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 1px;
                    background: #dee2e6;
                    border-radius: 8px;
                    overflow: hidden;
                }
                .calendar-day-header {
                    background: #6c757d;
                    color: white;
                    padding: 10px;
                    text-align: center;
                    font-weight: bold;
                    font-size: 0.9em;
                }
                .calendar-day {
                    background: white;
                    min-height: 80px;
                    padding: 8px;
                    cursor: pointer;
                    position: relative;
                    color: #C38154;
                    transition: background-color 0.2s;
                }
                .calendar-day:hover:not(.empty) {
                    background: #f8f9fa;
                }
                .calendar-day.empty {
                    background: #f1f3f4;
                    cursor: default;
                }
                .calendar-day.today {
                    background: #e3f2fd;
                    border: 2px solid #2196f3;
                }
                .calendar-day.has-menu {
                    background: #f0f9ff;
                }
                .day-number {
                    font-weight: bold;
                    margin-bottom: 5px;
                }
                .menu-indicators {
                    display: flex;
                    gap: 2px;
                    flex-wrap: wrap;
                }
                .btn-nav {
                    background: #c38154;
                    border: none;
                    color: white;
                    padding: 8px 15px;
                    border-radius: 5px;
                }
                .btn-nav:hover {
                    background: #a0673f;
                    color: white;
                }
            `}</style>

            <div className="calendar-container">
                <h4 className="text-center mb-4" style={{color: '#c38154'}}>Menu Calendar</h4>
                
                <Card>
                    <Card.Header className="calendar-header" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <Button 
                            className="btn-nav"
                            onClick={() => navigateMonth(-1)}
                        >
                            &lt; Previous
                        </Button>
                        <h5 className="mb-0" style={{color: '#c38154'}}>
                            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </h5>
                        <Button 
                            className="btn-nav"
                            onClick={() => navigateMonth(1)}
                        >
                            Next &gt;
                        </Button>
                    </Card.Header>
                    
                    <Card.Body className="p-0">
                        <div className="calendar-grid">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                <div key={day} className="calendar-day-header">{day}</div>
                            ))}
                            {renderCalendar()}
                        </div>
                    </Card.Body>
                </Card>

                <div className="mt-3 text-center">
                    <small className="text-muted">
                        <Badge bg="warning">L</Badge> Lunch Available &nbsp;
                        <Badge bg="info">D</Badge> Dinner Available &nbsp;
                        <Badge bg="secondary">X</Badge> No Thaali
                    </small>
                </div>
            </div>

            {/* Menu Edit Modal */}
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedDate ? `Menu for ${selectedDate.toDateString()}` : 'Edit Menu'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
                        <Tab eventKey="lunch" title="🌅 Lunch">
                            <Form.Check
                                type="checkbox"
                                label="No lunch thaali"
                                checked={noLunchThaali}
                                onChange={(e) => {
                                    setNoLunchThaali(e.target.checked);
                                    if (e.target.checked) setLunchItems(['']);
                                }}
                                className="mb-3"
                            />
                            
                            {noLunchThaali ? (
                                <Form.Group>
                                    <Form.Label>Lunch Comment</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={lunchComment}
                                        onChange={(e) => setLunchComment(e.target.value)}
                                        placeholder="Urs / Miqaat / Other Events"
                                    />
                                </Form.Group>
                            ) : (
                                <>
                                    {lunchItems.map((item, index) => (
                                        <div key={index} className="d-flex mb-2">
                                            <Form.Control
                                                type="text"
                                                value={item}
                                                onChange={(e) => updateLunchItem(index, e.target.value)}
                                                placeholder={`Lunch item ${index + 1}`}
                                            />
                                            {lunchItems.length > 1 && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="ms-2"
                                                    onClick={() => removeLunchItem(index)}
                                                >
                                                    <BsTrashFill />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={addLunchItem}
                                    >
                                        <BsFillPlusSquareFill className="me-1" /> Add Item
                                    </Button>
                                </>
                            )}
                        </Tab>
                        
                        <Tab eventKey="dinner" title="🌙 Dinner">
                            <Form.Check
                                type="checkbox"
                                label="No dinner thaali"
                                checked={noDinnerThaali}
                                onChange={(e) => {
                                    setNoDinnerThaali(e.target.checked);
                                    if (e.target.checked) setDinnerItems(['']);
                                }}
                                className="mb-3"
                            />
                            
                            {noDinnerThaali ? (
                                <Form.Group>
                                    <Form.Label>Dinner Comment</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={dinnerComment}
                                        onChange={(e) => setDinnerComment(e.target.value)}
                                        placeholder="Urs / Miqaat / Other Events"
                                    />
                                </Form.Group>
                            ) : (
                                <>
                                    {dinnerItems.map((item, index) => (
                                        <div key={index} className="d-flex mb-2">
                                            <Form.Control
                                                type="text"
                                                value={item}
                                                onChange={(e) => updateDinnerItem(index, e.target.value)}
                                                placeholder={`Dinner item ${index + 1}`}
                                            />
                                            {dinnerItems.length > 1 && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="ms-2"
                                                    onClick={() => removeDinnerItem(index)}
                                                >
                                                    <BsTrashFill />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={addDinnerItem}
                                    >
                                        <BsFillPlusSquareFill className="me-1" /> Add Item
                                    </Button>
                                </>
                            )}
                        </Tab>
                    </Tabs>
                </Modal.Body>
                <Modal.Footer>
                    {menus[selectedDate?.toDateString()] && (
                        <Button variant="danger" onClick={deleteMenu} disabled={saving}>
                            Delete Menu
                        </Button>
                    )}
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={saveMenu} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Menu'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MenuCalendar;